/** /orders/:id/:tab? — the order record: one order, read and edited in place on the
 *  booking record's structure (plan §1.1 "same for /orders", §2.4, D10).
 *
 *  - **Page tabs** (URL): Order (the spine view — the only editable surface) · Activity
 *    · Integration · Diagnose (console_entity_ai). The draft and the ledger are
 *    PAGE-level: switching tabs keeps both, the ledger stays docked on every tab while
 *    editing, and the unsaved-changes guard only fires on leaving the record
 *    (`basePath=/orders/<id>`). The old overview's Import and API Requests tabs live in
 *    Integration now.
 *  - **The spine**: Placed · Contact · Bookings · Money, sticky under the tabs; `1`–`4`
 *    jump to the panels. Times are UTC, labelled — an order spans its bookings' tours.
 *  - **Edit mode** in place: `Edit` / `E` (or `?edit=1`) flips the Order tab's FieldSlots
 *    and docks the ledger; Esc / Close editor asks first when the draft is dirty. No
 *    console_orders_edit, or the ConsoleOrderEditEnabled kill switch off → view mode
 *    only. An order-ref change raises the required "Linked bookings" question (D10) —
 *    under the field and in the ledger — and Save waits for it.
 *  - A draft restored from sessionStorage (`console:draft:order:<id>`) — or kept after
 *    leaving edit mode — is announced in view mode too.
 *  - States: skeleton panels while loading, a slim progress line on a background refetch,
 *    standard empty states for a missing order or one the operator may not see.
 */
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { Alert, Box, Button } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';

import {
  useOrderEditContext,
  useOrderRecordCatalog,
  type OrderRecordCatalog,
  type OrderRecordEditContext,
  type OrderRecordStage,
} from '@/api/orderRecord';
import * as ordersApi from '@/api/orders';
import { SpineNav } from '@/features/bookings/record/views/SpineNav';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { Ledger } from '@/features/records/ledger/Ledger';
import { LedgerDock, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  missingStatus,
  prefersReducedMotion,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav,
} from '@/features/records/page/recordPage';
import { RecordSkeleton } from '@/features/records/page/RecordStates';
import { usePermissions } from '@/hooks/usePermissions';
import { formatZoned } from '@/utils/zonedTime';

import { AiDiagnosePanel } from './diagnose/AiDiagnosePanel';
import { BOOKINGS_EDIT_PERM, useOrderRecordEditorConfig } from './record/editorConfig';
import { useOrderImmediateActions } from './record/useOrderImmediateActions';
import { OrderRecordHeader } from './record/OrderRecordHeader';
import { OrderNoPermission, OrderNotFound } from './record/OrderRecordStates';
import { OrderActivityTab } from './record/tabs/ActivityTab';
import { OrderIntegrationTab } from './record/tabs/IntegrationTab';
import { orderManagedBy, orderStageAnchorId, type OrderRecordData } from './record/views/spineLayout';
import { ORDER_TIMEZONE, orderSpineModel } from './record/views/spineModel';
import { OrderSpineView, useOrderSpineMarks } from './record/views/SpineView';

export type OrderTabId = 'order' | 'activity' | 'integration' | 'diagnose';

const TABS: { id: OrderTabId; label: string; perm?: string }[] = [
  { id: 'order', label: 'Order' },
  { id: 'activity', label: 'Activity' },
  { id: 'integration', label: 'Integration' },
  { id: 'diagnose', label: 'Diagnose', perm: 'console_entity_ai' },
];
const TABS_LABELS = { label: 'Order sections', refreshingLabel: 'Refreshing the order' };

const ORDER_STAGES: OrderRecordStage[] = ['placed', 'contact', 'bookings', 'money'];

export function OrderDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) return <OrderNotFound id={idStr ?? ''} />;
  return <OrderRecordPage key={id} id={id} tabParam={tab} />;
}

function OrderRecordPage({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const { hasPerm } = usePermissions();
  const nav = useRecordNav<OrderTabId>(`/orders/${id}`, 'order');
  const [params] = useSearchParams();

  const detailQ = useQuery({
    queryKey: ordersApi.orderDetailKey(id),
    queryFn: () => ordersApi.retrieve(id),
    retry: retryUnlessMissing,
  });
  const missing = missingStatus(detailQ.error);
  const catalogQ = useOrderRecordCatalog();
  const ctxQ = useOrderEditContext(id, { enabled: missing === null });

  const tabs = TABS.filter((t) => !t.perm || hasPerm(t.perm));
  const activeTab: OrderTabId = tabs.find((t) => t.id === tabParam)?.id ?? 'order';
  const section = tabs.find((t) => t.id === activeTab)?.label ?? 'Order';

  if (missing === 404) return <OrderNotFound id={id} />;
  if (missing === 403) return <OrderNoPermission id={id} />;

  const detail = detailQ.data;
  const catalog = catalogQ.data;
  const editContext = ctxQ.data;
  if (!detail || !catalog || !editContext) {
    const failed = detailQ.error ?? catalogQ.error ?? ctxQ.error;
    return (
      <Box>
        <OrderRecordHeader
          id={id}
          detail={detail}
          editContext={editContext}
          section={section}
          canEdit={false}
          editing={false}
          editingDisabled={false}
          onEdit={() => undefined}
          onClose={() => undefined}
          integrationHref={nav.hrefFor('integration')}
        />
        <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={nav.hrefFor} fetching={false} {...TABS_LABELS} />
        {failed ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void detailQ.refetch();
                  void catalogQ.refetch();
                  void ctxQ.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Couldn’t load order #{id}
            {failed instanceof Error && failed.message ? ` — ${failed.message}` : ''}.
          </Alert>
        ) : (
          <RecordSkeleton label="Loading the order" nodes={4} />
        )}
      </Box>
    );
  }

  const editingDisabled = !(catalog.enabled && editContext.flags.order_edit_enabled);
  const canEdit = hasPerm('console_orders_edit') && editContext.permissions.console_orders_edit && !editingDisabled;

  return (
    <OrderRecordLoaded
      id={id}
      tabs={tabs}
      activeTab={activeTab}
      section={section}
      detail={detail}
      catalog={catalog}
      editContext={editContext}
      canEdit={canEdit}
      canEditBookings={hasPerm(BOOKINGS_EDIT_PERM)}
      editing={canEdit && params.get('edit') === '1'}
      editingDisabled={editingDisabled}
      fetching={detailQ.isFetching || ctxQ.isFetching}
      nav={nav}
    />
  );
}

interface LoadedProps {
  id: number;
  tabs: { id: OrderTabId; label: string }[];
  activeTab: OrderTabId;
  section: string;
  detail: ordersApi.OrderDetail;
  catalog: OrderRecordCatalog;
  editContext: OrderRecordEditContext;
  canEdit: boolean;
  canEditBookings: boolean;
  editing: boolean;
  editingDisabled: boolean;
  fetching: boolean;
  nav: RecordNav<OrderTabId>;
}

interface PendingFocus {
  rec: string;
  path: string;
}

function OrderRecordLoaded(props: LoadedProps) {
  const { id, activeTab, detail, catalog, editContext, editing, canEditBookings, nav } = props;
  // A ledger row for a field that is not on screen (another tab): back to the Order tab,
  // then focus it once its FieldSlot has mounted.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const tabRef = useRef(activeTab);
  tabRef.current = activeTab;
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: string, path: string) => {
      if (tabRef.current === 'order') return;
      pendingFocus.current = { rec, path };
      goTab('order');
    },
    [goTab],
  );
  const config = useOrderRecordEditorConfig({ catalog, editContext, detail, canEditBookings, onFocusMissing });
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);

  return (
    <DraftProvider kind="order" recordId={id} baselines={editContext.baselines} baselineTs={editContext.generated_ts}>
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <OrderRecordShell {...props} pendingFocus={pendingFocus} />
      </RecordEditorProvider>
    </DraftProvider>
  );
}

function OrderRecordShell({
  id,
  tabs,
  activeTab,
  section,
  detail,
  catalog,
  editContext,
  canEdit,
  editing,
  editingDisabled,
  fetching,
  nav,
  pendingFocus,
}: LoadedProps & { pendingFocus: MutableRefObject<PendingFocus | null> }) {
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  const { prompt } = useDraftUnsavedGuard(`/orders/${id}`);
  const marks = useOrderSpineMarks();
  const data = useMemo<OrderRecordData>(() => ({ catalog, editContext, detail }), [catalog, editContext, detail]);
  const model = useMemo(() => orderSpineModel(detail), [detail]);
  const { setParam, goTab } = nav;
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);
  const immediate = useOrderImmediateActions({
    orderId: id,
    tenantId: detail.tenant?.id,
    onDiagnose: () => goTab('diagnose'),
  });

  // The spine's height places the ledger's side column under it and clears it when a
  // node scrolls a panel into view.
  const spineRef = useRef<HTMLDivElement | null>(null);
  const [spineHeight, setSpineHeight] = useState(0);
  const onOrder = activeTab === 'order';
  useLayoutEffect(() => {
    const el = spineRef.current;
    if (!el) {
      setSpineHeight(0);
      return undefined;
    }
    const measure = () => setSpineHeight(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onOrder]);
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();

  const focusStage = useCallback((stage: OrderRecordStage) => {
    const panel = document.getElementById(orderStageAnchorId(stage));
    if (!panel) return;
    panel.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    panel.focus({ preventScroll: true });
  }, []);

  useRecordShortcuts(
    {
      ...(canEdit && !editing ? { onEdit: enterEdit } : {}),
      ...(onOrder
        ? {
            onStage: (i: number) => {
              const stage = ORDER_STAGES[i];
              if (stage) focusStage(stage);
            },
          }
        : {}),
    },
    { stageCount: ORDER_STAGES.length },
  );

  useEffect(() => {
    if (!onOrder || !pendingFocus.current) return undefined;
    // Taken when the frame runs, not before: an editor update in between (a preview
    // arriving) re-runs this effect, whose cleanup cancels the frame — the pending focus
    // must survive that to be tried again.
    const raf = requestAnimationFrame(() => {
      const pending = pendingFocus.current;
      pendingFocus.current = null;
      if (pending) editor.focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [onOrder, editor, pendingFocus]);

  const stickyTop = (onOrder ? spineHeight : 0) - SCROLLER_PADDING_PX + DOCK_GAP_PX;

  return (
    <Box sx={{ '--record-spine-height': `${spineHeight + DOCK_GAP_PX}px` }} data-record-page={id}>
      <OrderRecordHeader
        id={id}
        detail={detail}
        editContext={editContext}
        section={section}
        canEdit={canEdit}
        editing={editing}
        editingDisabled={editingDisabled}
        onEdit={enterEdit}
        onClose={editor.requestClose}
        integrationHref={nav.hrefFor('integration')}
        actionGroups={immediate.groups}
      />

      {!editing && count > 0 && (
        <Alert
          severity="info"
          icon={<EditNoteOutlinedIcon />}
          sx={{ mb: 2 }}
          data-view-draft-banner
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && (
                <Button color="inherit" size="small" onClick={enterEdit}>
                  Continue editing
                </Button>
              )}
              <Button
                color="inherit"
                size="small"
                onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}
              >
                Discard
              </Button>
            </Box>
          }
        >
          {restored
            ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, ORDER_TIMEZONE, { weekday: false })} — not saved yet.`
            : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
        </Alert>
      )}

      <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={nav.hrefFor} fetching={fetching} {...TABS_LABELS} />

      {onOrder && (
        <SpineNav
          ref={spineRef}
          label="Order lifecycle"
          model={model}
          editing={editing}
          marks={marks}
          managedBy={orderManagedBy(editContext)}
          onSelect={focusStage}
          compactMarks={contentWidth > 0 && contentWidth < SHEET_BELOW_PX}
        />
      )}

      <Box ref={contentRef}>
        <LedgerDock
          open={editing}
          stickyTop={stickyTop}
          viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
          ledger={(variant) => <Ledger variant={variant} />}
        >
          {activeTab === 'order' && <OrderSpineView data={data} width={contentWidth} />}
          {activeTab === 'activity' && <OrderActivityTab orderId={id} detail={detail} />}
          {activeTab === 'integration' && <OrderIntegrationTab orderId={id} />}
          {activeTab === 'diagnose' && <AiDiagnosePanel orderId={id} tenantId={detail.tenant?.id} />}
        </LedgerDock>
      </Box>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to="/orders" style={{ color: '#0a1a3f' }}>
          ← Back to orders
        </RouterLink>
      </Box>
      {immediate.host}
      {prompt}
    </Box>
  );
}
